/* eslint-disable @typescript-eslint/no-explicit-any */
const findInObject = (
  obj: { [x: string]: any },
  comparedObj: string,
  fields?: any[],
  visited = new WeakSet(),
  depth = 0
): boolean => {
  // Prevent infinite recursion with depth limit and circular reference detection
  if (depth > 5 || !obj || typeof obj !== 'object' || visited.has(obj)) {
    return false;
  }

  visited.add(obj);

  return Object.keys(obj).some(function (key) {
    if (fields && fields.length > 0 && !fieldCheck(key, fields)) return false;
    const value = obj[key];
    if (!value) {
      return false;
    }
    if (typeof value === 'string') {
      return value.toLowerCase().indexOf(comparedObj.toLowerCase()) > -1;
    }

    if (value instanceof Array) {
      return value.some(function (e) {
        if (typeof e === 'string') {
          return e.toLowerCase().indexOf(comparedObj.toLowerCase()) > -1;
        }
        if (typeof e === 'object' && e !== null) {
          return findInObject(e, comparedObj, fields, visited, depth + 1);
        }
        return false;
      });
    }

    // For other object types, recurse safely with depth and circular reference protection
    if (typeof value === 'object' && value !== null) {
      return findInObject(value, comparedObj, fields, visited, depth + 1);
    }

    return false;
  });
};

const fieldCheck = (objField: string, fields: any[]) => {
  return fields.find((element) => {
    return objField === element;
  });
};

const searchArray = (array: any[], searchString: string) => {
  if (!searchString || '' === searchString.trim()) return [...array];
  const searchValue = searchString.toLowerCase();
  return array.filter((item) => {
    return findInObject(item, searchValue);
  });
};

const searchArrayByFields = (
  array: any[],
  searchString: string,
  searchFields: any[] | undefined
) => {
  if (!searchString || '' === searchString.trim()) return [...array];
  const searchValue = searchString.toLowerCase();
  return array.filter((item) => {
    return findInObject(item, searchValue, searchFields);
  });
};

const searchArrayWithContext = (
  array: any[],
  searchContext: { searchString: any; searchFields: any }
) => {
  const searchString = searchContext.searchString;
  if (!searchString || '' === searchString.trim()) return [...array];
  const searchFields = searchContext.searchFields;
  const searchValue = searchString.toLowerCase();
  const retArray = array.filter((item) => {
    return findInObject(item, searchValue, searchFields);
  });
  return retArray;
};

const applyAllFilters = (
  allObjects: any[],
  searchString: string,
  objectFunc: (arg0: any[]) => void
) => {
  if (allObjects.length > 0) {
    objectFunc(searchArrayByFields(allObjects, searchString, []));
  } else {
    objectFunc(allObjects);
  }
};

const compareObjects = (
  obj1: Record<string, unknown> | null | undefined,
  obj2: Record<string, unknown> | null | undefined,
  fieldsToCompare?: string[] | Set<string>
) => {
  const changes: {
    field: string;
    currentValue: unknown;
    originalValue: unknown;
  }[] = [];

  // Handle null/undefined cases
  if (!obj1 && !obj2) return changes;
  if (!obj1 || !obj2) {
    // One is null/undefined, the other isn't - everything is different
    const sourceObj = (obj1 || obj2) as Record<string, unknown>;
    const keys = fieldsToCompare
      ? (Array.isArray(fieldsToCompare)
          ? fieldsToCompare
          : Array.from(fieldsToCompare)
        ).filter((key) => key in sourceObj)
      : Object.keys(sourceObj);

    keys.forEach((key) => {
      changes.push({
        field: key,
        currentValue: obj1?.[key],
        originalValue: obj2?.[key],
      });
    });
    return changes;
  }

  // Get keys to compare - either specified fields or all keys from both objects
  let keysToCheck: Set<string>;
  if (fieldsToCompare) {
    keysToCheck = Array.isArray(fieldsToCompare)
      ? new Set(fieldsToCompare)
      : new Set(fieldsToCompare);
  } else {
    keysToCheck = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  }

  keysToCheck.forEach((key) => {
    const currentValue = obj1[key];
    const originalValue = obj2[key];

    // Compare values (handles primitives, but you might need deeper comparison for nested objects)
    if (currentValue !== originalValue) {
      changes.push({
        field: key,
        currentValue,
        originalValue,
      });
    }
  });

  return changes;
};

export {
  searchArray,
  searchArrayByFields,
  searchArrayWithContext,
  applyAllFilters,
  compareObjects,
};
